import { useEffect, useState } from 'react'
import { dimensionOptionsApi, EMPTY_DIMENSION_OPTIONS_RESPONSE, type DimensionOption, type DimensionOptionsResponse } from '../api/costs'
import { userDirectoryApi } from '../api/userDirectory'

/**
 * R3-10 (flag finanza.dimensioni_estese): le dimensioni estese di spese, centri di costo e movimenti. Le liste di BU e sede
 * vengono dal server; una lista senza voci attive e' "nascosta" (`visible: false`) e la UI non mostra il campo. Il team e' il
 * responsabile di una squadra: si sceglie fra gli utenti che hanno dei subordinati (se l'elenco utenti non e' leggibile
 * dal ruolo, il campo ripiega sull'inserimento dell'id).
 */
export interface FinanceDimensions {
  enabled: boolean
  businessUnits: DimensionOption[]
  sites: DimensionOption[]
  teams: DimensionOption[]
  /** Etichetta di un id (o `#id` se la voce non e' piu' attiva), `null` se non assegnato. */
  label: (kind: 'bu' | 'sede' | 'team', id?: number | null) => string | null
}

const EMPTY: FinanceDimensions = { enabled: false, businessUnits: [], sites: [], teams: [], label: () => null }

export async function loadFinanceDimensions(): Promise<FinanceDimensions> {
  const options: DimensionOptionsResponse = await dimensionOptionsApi.list(true).catch(() => EMPTY_DIMENSION_OPTIONS_RESPONSE)
  if (!options.enabled) return EMPTY
  const users = await userDirectoryApi.list({ activeOnly: true, limit: 500 }).catch(() => [])
  const leaderIds = new Set(users.map(u => u.parentUserId).filter((x): x is number => x != null))
  const toOption = (o: { id: number; name: string }): DimensionOption => ({ id: o.id, label: o.name })
  const businessUnits = options.businessUnits.items.filter(o => o.isActive).map(toOption)
  const sites = options.sites.items.filter(o => o.isActive).map(toOption)
  const teams = users.filter(u => leaderIds.has(u.id)).map(u => ({ id: u.id, label: u.name }))
  const all = {
    bu: options.businessUnits.items.map(toOption),
    sede: options.sites.items.map(toOption),
    team: users.map(u => ({ id: u.id, label: u.name })),
  }
  return {
    enabled: true,
    // Una lista senza voci attive e' nascosta.
    businessUnits: options.businessUnits.visible ? businessUnits : [],
    sites: options.sites.visible ? sites : [],
    teams,
    label: (kind, id) => (id ? all[kind].find(o => o.id === id)?.label ?? `#${id}` : null),
  }
}

export function useFinanceDimensions(): FinanceDimensions {
  const [dims, setDims] = useState<FinanceDimensions>(EMPTY)
  useEffect(() => {
    let alive = true
    void loadFinanceDimensions().then(d => { if (alive) setDims(d) }).catch(() => { /* resta spento */ })
    return () => { alive = false }
  }, [])
  return dims
}
